import React, { useState } from 'react';
import { Button, FadeIn, Logo, Field } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../api/client';

// Name (+ optional address) collected before a new customer enters the app.
export default function CustomerOnboarding() {
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState('name'); // 'name' | 'address'
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [referral, setReferral] = useState('');
  const [addr, setAddr] = useState({ flat: '', building: '', street: '', area: '', city: '', state: '', pincode: '' });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  const saveName = async (e) => {
    e?.preventDefault();
    if (!first.trim()) return toast('Please enter your first name', { tone: 'error' });
    setLoading(true);
    try {
      await api.put('/users/me', {
        name: `${first.trim()} ${last.trim()}`.trim(),
        sponsorCode: referral.trim().toUpperCase() || undefined,
      });
      setStep('address');
    } catch (err) {
      toast(err.message || 'Please try again', { title: 'Could not save', tone: 'error' });
    }
    setLoading(false);
  };

  const finish = async (withAddress) => {
    setLoading(true);
    try {
      if (withAddress) {
        await api.post('/users/me/addresses', {
          label: 'Home',
          line1: [addr.flat, addr.building].filter(Boolean).join(', '),
          line2: [addr.street, addr.area].filter(Boolean).join(', '),
          city: addr.city,
          state: addr.state,
          pincode: addr.pincode,
          isDefault: true,
        });
      }
      await refreshUser(); // now has a name → the gate lets us into the app
    } catch (err) {
      toast(err.message || 'Please try again', { title: 'Could not save address', tone: 'error' });
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const canFinishAddress = addr.flat && addr.city && addr.pincode.length === 6;

  return (
    <div className="app-shell">
      <div className="screen screen--plain" style={{ padding: 'calc(var(--sat) + 22px) 22px 40px', maxWidth: 520, margin: '0 auto', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}><Logo size="md" /></div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, margin: '8px 0 20px' }}>
          <span style={{ width: 26, height: 5, borderRadius: 999, background: 'var(--primary)' }} />
          <span style={{ width: 26, height: 5, borderRadius: 999, background: step === 'address' ? 'var(--primary)' : 'var(--border)' }} />
        </div>

        {step === 'name' ? (
          <FadeIn from={12}>
            <h1 style={{ fontSize: 24, fontWeight: 800, textAlign: 'center', margin: 0 }}>What's your name?</h1>
            <p className="muted center" style={{ marginTop: 6, marginBottom: 20 }}>Tell us who we're serving.</p>

            <form onSubmit={saveName}>
              <Field label="First name" value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" autoComplete="given-name" />
              <Field label="Last name" value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last name" autoComplete="family-name" />
              <Field
                label="Referral code (optional)" value={referral} maxLength={10}
                onChange={(e) => setReferral(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())}
                placeholder="Got a code from a friend?"
              />
              <Button type="submit" title={loading ? 'Saving…' : 'Continue'} disabled={loading} style={{ marginTop: 22 }} />
            </form>
          </FadeIn>
        ) : (
          <FadeIn from={12}>
            <h1 style={{ fontSize: 24, fontWeight: 800, textAlign: 'center', margin: 0 }}>Add your address</h1>
            <p className="muted center" style={{ marginTop: 6, marginBottom: 20 }}>
              This helps us serve you faster. You can skip and add it later.
            </p>

            <div className="field-row">
              <Field label="Flat / House no." value={addr.flat} onChange={set('flat')} placeholder="Flat no." />
              <Field label="Building" value={addr.building} onChange={set('building')} placeholder="Building name" />
            </div>
            <Field label="Street / Road" value={addr.street} onChange={set('street')} placeholder="Street / road" />
            <Field label="Area / Locality" value={addr.area} onChange={set('area')} placeholder="Area / locality" />
            <div className="field-row">
              <div style={{ flex: 1.4 }}>
                <Field label="City" value={addr.city} onChange={set('city')} placeholder="City" />
              </div>
              <div style={{ flex: 1 }}>
                <Field
                  label="Pincode" value={addr.pincode} inputMode="numeric" maxLength={6} placeholder="Pincode"
                  onChange={(e) => setAddr((a) => ({ ...a, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                />
              </div>
            </div>
            <Field label="State" value={addr.state} onChange={set('state')} placeholder="State" />

            <Button title={loading ? 'Saving…' : 'Save & Continue'} onClick={() => finish(true)}
              disabled={!canFinishAddress || loading} style={{ marginTop: 22 }} />
            <Button variant="ghost" title="Skip for now" onClick={() => finish(false)} disabled={loading} style={{ marginTop: 12 }} />
          </FadeIn>
        )}
      </div>
    </div>
  );
}
